import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, RefreshCw, RotateCcw, Send, UserCircle, Volume2, VolumeX, Wifi, WifiOff } from 'lucide-react';
import { getAccountDetails, getChatConversations, getChatMessages, markChatRead, resetAIContext, sendChatMessage } from '../services/api';
import { AccountDetail, ChatMessage } from '../types';

const conversationKey = (message: ChatMessage) => `${message.cookie_id}:${message.chat_id}`;

const Chat: React.FC = () => {
  const [conversations, setConversations] = useState<ChatMessage[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [activeConversation, setActiveConversation] = useState<ChatMessage | null>(null);
  const activeRef = useRef<ChatMessage | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('chat_sound_enabled') !== 'false');
  const soundEnabledRef = useRef(soundEnabled);
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastSoundAtRef = useRef(0);
  const initialConversationLoadedRef = useRef(false);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    localStorage.setItem('chat_sound_enabled', String(soundEnabled));
  }, [soundEnabled]);

  const getAudioContext = useCallback(() => {
    const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => undefined);
    return audioContextRef.current;
  }, []);

  const playNotification = useCallback(() => {
    if (!soundEnabledRef.current || Date.now() - lastSoundAtRef.current < 900) return;
    const context = getAudioContext();
    if (!context) return;
    lastSoundAtRef.current = Date.now();
    const start = context.currentTime;
    [
      { frequency: 880, offset: 0 },
      { frequency: 660, offset: 0.14 },
    ].forEach(({ frequency, offset }) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start + offset);
      gain.gain.exponentialRampToValueAtTime(0.12, start + offset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.11);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start + offset);
      oscillator.stop(start + offset + 0.12);
    });
  }, [getAudioContext]);

  useEffect(() => {
    const unread = conversations.reduce((total, conversation) => total + (conversation.unread_count || 0), 0);
    document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) 闲鱼客服` : '闲鱼客服';
    return () => {
      document.title = '闲鱼客服';
    };
  }, [conversations]);

  useEffect(() => {
    activeRef.current = activeConversation;
  }, [activeConversation]);

  const mergeConversation = useCallback((incoming: ChatMessage) => {
    setConversations((current) => {
      const key = conversationKey(incoming);
      const existing = current.find((item) => conversationKey(item) === key);
      const active = activeRef.current && conversationKey(activeRef.current) === key;
      const merged = {
        ...existing,
        ...incoming,
        unread_count: incoming.sender_role === 'buyer' && !active
          ? (existing?.unread_count || 0) + 1
          : (active ? 0 : existing?.unread_count || 0),
      };
      return [merged, ...current.filter((item) => conversationKey(item) !== key)];
    });
  }, []);

  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    if (socketRef.current && socketRef.current.readyState <= WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${window.location.host}/ws/chat?token=${encodeURIComponent(token)}`);
    socketRef.current = socket;
    socket.onopen = () => setConnected(true);
    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type !== 'message' || !payload.data) return;
        const incoming = payload.data as ChatMessage;
        if (incoming.sender_role === 'buyer') playNotification();
        mergeConversation(incoming);
        const active = activeRef.current;
        if (active && conversationKey(active) === conversationKey(incoming)) {
          setMessages((current) => current.some((item) => item.id === incoming.id) ? current : [...current, incoming]);
          if (incoming.sender_role === 'buyer') {
            markChatRead(incoming.cookie_id, incoming.chat_id).catch(() => undefined);
          }
        }
      } catch (error) {
        console.warn('解析聊天推送失败', error);
      }
    };
    socket.onclose = () => {
      setConnected(false);
      socketRef.current = null;
      if (reconnectTimerRef.current === null) {
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          connectSocket();
        }, 3000);
      }
    };
    socket.onerror = () => setConnected(false);
  }, [mergeConversation, playNotification]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getChatConversations(), getAccountDetails()])
      .then(([chatList, accountList]) => {
        if (cancelled) return;
        setConversations(chatList);
        setAccounts(accountList);
        if (chatList.length > 0) setActiveConversation(chatList[0]);
      })
      .catch((error) => console.error('加载聊天失败', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    connectSocket();
    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [connectSocket]);

  const openConversation = async (conversation: ChatMessage) => {
    setActiveConversation(conversation);
    activeRef.current = conversation;
    setMessages([]);
    setMessagesLoading(true);
    try {
      const history = await getChatMessages(conversation.cookie_id, conversation.chat_id);
      setMessages(history);
      await markChatRead(conversation.cookie_id, conversation.chat_id);
      setConversations((current) => current.map((item) => conversationKey(item) === conversationKey(conversation)
        ? { ...item, unread_count: 0 }
        : item));
    } catch (error) {
      console.error('加载聊天记录失败', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && activeConversation && !initialConversationLoadedRef.current) {
      initialConversationLoadedRef.current = true;
      openConversation(activeConversation);
    }
  }, [loading, activeConversation]);

  const handleSend = async () => {
    const conversation = activeConversation;
    const text = draft.trim();
    if (!conversation || !text || sending) return;
    setSending(true);
    try {
      const result = await sendChatMessage({
        cookie_id: conversation.cookie_id,
        chat_id: conversation.chat_id,
        to_user_id: conversation.buyer_id,
        message: text,
        item_id: conversation.item_id,
        buyer_name: conversation.buyer_name,
      });
      if (!result.success || !result.data) throw new Error(result.message || '发送失败');
      setMessages((current) => current.some((item) => item.id === result.data!.id) ? current : [...current, result.data!]);
      mergeConversation(result.data);
      setDraft('');
    } catch (error: any) {
      alert(error?.response?.data?.detail || error?.message || '发送失败，请检查闲鱼账号是否在线');
    } finally {
      setSending(false);
    }
  };

  const toggleSound = () => {
    const nextEnabled = !soundEnabledRef.current;
    soundEnabledRef.current = nextEnabled;
    setSoundEnabled(nextEnabled);
    if (nextEnabled) playNotification();
  };

  const handleResetAIContext = async () => {
    if (!activeConversation || !window.confirm('只清空该会话的AI上下文？网页聊天记录不会删除。')) return;
    try {
      await resetAIContext(activeConversation.cookie_id, activeConversation.chat_id);
      window.alert('AI上下文已清空，下一条消息将开始新的上下文。');
    } catch (error: any) {
      window.alert(error?.response?.data?.detail || error?.message || '清空AI上下文失败');
    }
  };

  const accountName = (cookieId: string) => accounts.find((account) => account.id === cookieId)?.nickname || cookieId;

  return (
    <div className="space-y-6 animate-fade-in h-[calc(100vh-7rem)] min-h-[620px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">实时客服</h2>
          <p className="text-gray-500 mt-2 text-sm">接收闲鱼买家消息，并从网页直接回复。</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleSound} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title={soundEnabled ? '关闭新消息提示音' : '开启新消息提示音'}>
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold ${connected ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {connected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {connected ? '实时连接中' : '等待连接'}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden flex h-[calc(100%-5rem)]">
        <aside className="w-[340px] border-r border-gray-100 flex flex-col shrink-0">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div className="font-bold text-gray-900 flex items-center gap-2"><MessageCircle className="w-5 h-5" />会话</div>
            <button onClick={() => getChatConversations().then(setConversations)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="刷新">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400">加载中...</div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-400">暂无聊天消息<br /><span className="text-xs">等待闲鱼买家发消息</span></div>
            ) : conversations.map((conversation) => {
              const active = activeConversation && conversationKey(activeConversation) === conversationKey(conversation);
              return (
                <button key={conversationKey(conversation)} onClick={() => openConversation(conversation)} className={`w-full text-left p-4 border-b border-gray-50 transition-colors ${active ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    <UserCircle className="w-10 h-10 text-gray-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between gap-2">
                        <div className="font-bold text-gray-800 truncate">{conversation.buyer_name || conversation.buyer_id || '买家'}</div>
                        {conversation.unread_count ? <span className="bg-red-500 text-white text-[10px] min-w-5 h-5 px-1 rounded-full flex items-center justify-center">{conversation.unread_count > 99 ? '99+' : conversation.unread_count}</span> : null}
                      </div>
                      <div className="text-xs text-gray-400 mt-1 truncate">{accountName(conversation.cookie_id)}</div>
                      <div className="text-sm text-gray-500 mt-2 truncate">{conversation.sender_role === 'seller' ? '我：' : ''}{conversation.content}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="flex-1 flex flex-col min-w-0">
          {!activeConversation ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">选择一个会话开始聊天</div>
          ) : (
            <>
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <div className="font-bold text-gray-900">{activeConversation.buyer_name || activeConversation.buyer_id || '买家'}</div>
                  <div className="text-xs text-gray-400 mt-1">账号：{accountName(activeConversation.cookie_id)} · 商品：{activeConversation.item_id || '未识别'}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleResetAIContext} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="清空AI上下文">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-400">{activeConversation.chat_id}</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#fafafa]">
                {messagesLoading ? <div className="text-center text-gray-400 py-8">加载聊天记录...</div> : messages.map((message) => (
                  <div key={message.id} className={`flex ${message.sender_role === 'seller' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${message.sender_role === 'seller' ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div className={`px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap break-words ${message.sender_role === 'seller' ? 'bg-[#FFE815] text-gray-900 rounded-br-md' : 'bg-white border border-gray-100 text-gray-800 rounded-bl-md'}`}>{message.content}</div>
                      <div className="text-[10px] text-gray-400 mt-1 px-1">{message.created_at || ''}{message.sender_role === 'seller' ? ' · 我' : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-100 bg-white">
                <div className="flex gap-3 items-end">
                  <textarea value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); handleSend(); } }} placeholder="输入回复，Enter发送，Shift+Enter换行" className="flex-1 resize-none rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-yellow-400 min-h-[48px] max-h-32" rows={1} />
                  <button onClick={handleSend} disabled={sending || !draft.trim()} className="h-12 px-5 rounded-2xl bg-gray-900 text-white font-bold flex items-center gap-2 disabled:opacity-40 hover:bg-gray-800">
                    <Send className="w-4 h-4" />{sending ? '发送中' : '发送'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default Chat;
